import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import { useConfirm } from "../context/ConfirmContext";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";

/* ------------------------------------------------------------------ */
/*  Company Form Modal                                                 */
/* ------------------------------------------------------------------ */
function CompanyFormModal({ company, onClose, onRefresh }) {
  const isEdit = !!company;
  const [name, setName] = useState(company?.name || "");
  const [prefix, setPrefix] = useState(company?.prefix || "");
  const [isActive, setIsActive] = useState(company?.is_active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Company name is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      if (isEdit) {
        await api.put(`/companies/${company.id}`, { name: name.trim(), prefix: prefix.trim() || null, is_active: isActive });
      } else {
        await api.post("/companies", { name: name.trim(), prefix: prefix.trim() || null });
      }
      onRefresh();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} company.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b bg-gray-50 rounded-t-xl flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{isEdit ? "Edit Company" : "Add Company"}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{isEdit ? "Update company details" : "Create a new company record"}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none p-1">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              placeholder="e.g. BEST"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Prefix</label>
            <input
              type="text"
              value={prefix}
              onChange={(e) => { setPrefix(e.target.value); setError(""); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              placeholder="e.g. BEST"
            />
            <p className="text-xs text-gray-500 mt-1">Used as default prefix when creating teams (e.g. BEST-0024).</p>
          </div>

          {isEdit && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
            >
              {submitting ? "Saving…" : isEdit ? "Save Changes" : "Create Company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Companies Page                                                */
/* ------------------------------------------------------------------ */
export default function CompaniesPage() {
  const { isAdmin, hasPerm } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { confirm } = useConfirm();

  const canView = hasPerm("companies.view");
  const canCreate = hasPerm("companies.create");
  const canEdit = hasPerm("companies.edit");
  const canDelete = hasPerm("companies.delete");

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editCompany, setEditCompany] = useState(null);

  useEffect(() => {
    if (!canView) navigate("/dashboard", { replace: true });
  }, [canView, navigate]);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch {
      setError("Failed to load companies.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleDelete = async (company) => {
    const confirmed = await confirm({
      title: "Delete Company",
      message: `Are you sure you want to delete "${company.name}"? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    setActionError("");
    try {
      await api.delete(`/companies/${company.id}`);
      await fetchCompanies();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to delete company.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      <main className="flex-1 p-4 lg:p-6 max-w-[1200px] w-full mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
            <p className="text-sm text-gray-500 mt-1">Manage company records used across user accounts.</p>
          </div>
          {canCreate && (
            <button
              onClick={() => { setEditCompany(null); setShowForm(true); }}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Company
            </button>
          )}
        </div>

        {/* Errors */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
            {error}
          </div>
        )}
        {actionError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4 flex items-center">
            {actionError}
            <button onClick={() => setActionError("")} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <span className="text-sm text-gray-500">Loading companies…</span>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Prefix</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  {(canEdit || canDelete) && (
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-400">No companies found.</td>
                  </tr>
                ) : (
                  companies.map((c) => (
                    <tr key={c.id} className={`hover:bg-gray-50/50 transition-colors ${!c.is_active ? "opacity-50" : ""}`}>
                      <td className="px-6 py-4 text-gray-800 font-medium">{c.name}</td>
                      <td className="px-6 py-4 text-gray-600">{c.prefix || <span className="text-gray-400 italic">—</span>}</td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                            c.is_active
                              ? "bg-green-50 text-green-700 ring-1 ring-green-200"
                              : "bg-red-50 text-red-600 ring-1 ring-red-200"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${c.is_active ? "bg-green-500" : "bg-red-400"}`} />
                          {c.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {(canEdit || canDelete) && (
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            {canEdit && (
                              <button
                                onClick={() => { setEditCompany(c); setShowForm(true); }}
                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md font-medium transition-colors"
                              >
                                Edit
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(c)}
                                className="text-xs bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1.5 rounded-md font-medium transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showForm && (
        <CompanyFormModal
          company={editCompany}
          onClose={() => { setShowForm(false); setEditCompany(null); }}
          onRefresh={fetchCompanies}
        />
      )}
    </div>
  );
}
